import inventory from '../../core/inventory';
import { colorSwatchHandle } from './colorSelector';
import { getSectionInnerHTML } from './utils';
import gallery from './gallery';

function getSectionsToRender() {
  return [
    {
      id: 'pdp-gallery',
      section: 'pdp-gallery-content',
      selector: '.shopify-section',
    },
    {
      id: 'pdp-atc-size-selector',
      section: 'pdp-atc-size-selector',
      selector: '.shopify-section',
    },
    {
      id: 'pdp-atc-price-tag',
      section: 'pdp-atc-price-tag',
      selector: '.shopify-section',
    },
    {
      id: 'pdp-atc-add-to-bag',
      section: 'pdp-atc-add-to-bag',
      selector: '.shopify-section',
    },
  ];
}

class CheckInventory {
  constructor() {
    this.inventoryData = null;
  }

  #buildColorSwatch = ({ availableColors, variant, index, currentVariantObj }) => {
    const anchorWrapper = document.createElement('li');
    const anchorColor = document.createElement('a');
    anchorColor.classList.add('pdp-c-atc-color-selector__swatch-item');
    anchorColor.id = `color_swatch_${availableColors[index].toLowerCase()}`;
    anchorColor.setAttribute('data-barcode', variant.barcode);
    anchorColor.setAttribute('data-variant-id', variant.id);
    anchorColor.setAttribute(
      'data-color',
      availableColors[index].toLowerCase().replace(/\b\w/g, (letter) => letter.toUpperCase()),
    );
    anchorColor.setAttribute(
      'title',
      availableColors[index].toLowerCase().replace(/\b\w/g, (letter) => letter.toUpperCase()),
    );
    anchorColor.innerHTML = `
      <img src="https://cdn.shopify.com/s/files/1/0070/8853/7651/t/8/assets/${
        variant.barcode
      }_SWT_48x48_crop_center.jpg?v=1755815944" height="48" width="48" loading="eager" alt="Variant colorswatch" data-fallback="isp-product-color-swatch-${availableColors[
        index
      ].toLowerCase()}" onload="this.style.display = 'flex';" onerror="this.parentNode.classList.add(this.dataset.fallback)">
    `;
    // Check if it is active variant
    if (currentVariantObj.options.map((item) => item.toLowerCase()).includes(availableColors[index].toLowerCase())) {
      anchorColor.classList.add('pdp-c-atc-color-selector__swatch-item--active');
    }
    anchorColor.addEventListener('click', colorSwatchHandle);
    anchorWrapper.appendChild(anchorColor);

    return anchorWrapper;
  };

  #revealHiddenElements = () => {
    const elementHidden1 = document.querySelector('form.pdp-c-atc-form');
    const elementHidden2 = document.querySelector('div.pdp-c-gallery');
    const elementHidden3 = document.querySelector('div.pdp-c-atc-price-tag');

    elementHidden1?.classList.remove('pdp-c-atc-form--hide');
    elementHidden2?.classList.remove('pdp-c-gallery--hide');
    elementHidden3?.classList.remove('pdp-c-atc-price-tag--hide');
  };

  /**
   * Update color swatches based on inventory and product data
   * @param {Object} inventoryData - inventory data fetched from inventory.js
   * @param {Object} productJsonData - product JSON data fetched from product.js
   * @param {Function} callback - callback function to execute after updating colors
   * @returns {Promise}
   */
  #updateColors = (inventoryData, productJsonData, callback = () => {}) => {
    const colorSwatchesList = document.querySelector('.pdp-c-atc-color-selector__swatch-list');
    let previousActiveColor = '';

    // Add extra color whether they have avaialble units
    const availableColors = [];
    const soldOutColors = {};

    // Read colors from data and populate available and sold out colors arrays
    Object.keys(inventoryData.size).forEach((key) => {
      // Extracts a precise color. Sometimes it can come as "WHT/PRT / XL instead of "WHITE / XL". So, we need the whole first part"
      const color = key
        .split(/\s\/\s/g)
        .at(0)
        .trim();

      if (inventoryData.size[key] > 0) {
        if (!availableColors.includes(color)) {
          availableColors.push(color);
        }
      }

      if (!Object.hasOwn(soldOutColors, color)) {
        soldOutColors[color] = 0;
      }
      soldOutColors[color] += inventoryData.size[key];
    });

    // Look for which color are already rendered and remove them from available colors
    const renderedColorSwatches = document.querySelectorAll('.pdp-c-atc-color-selector__swatch-item');
    renderedColorSwatches.forEach((colorSwatch) => {
      const existingColor = availableColors.findIndex(
        (color) => colorSwatch.dataset.color.toLowerCase() === color.toLowerCase(),
      );
      existingColor != -1 && availableColors.splice(existingColor, 1);
    });

    // Remove sold out colors on existing colors or color already rendered on page
    // If there weren't any sold out color, this forEach will be ignored
    Object.keys(soldOutColors).forEach((color) => {
      // Guard conditions
      if (soldOutColors[color] !== 0) return;

      // don't remove any color if there is just one rendered color and it's sold out and there were no available colors
      if (renderedColorSwatches.length == 1 && soldOutColors[color] == 0 && availableColors.length == 0) return;

      const colorSwatchToRemove = document.querySelector(
        `.pdp-c-atc-color-selector__swatch-item[id="color_swatch_${color.toLowerCase().replace(' ', '_')}"]`,
      );
      if (!colorSwatchToRemove) return;

      // if this color swatch is active and it is removed, we will store it as previous active color
      if (colorSwatchToRemove.classList.contains('pdp-c-atc-color-selector__swatch-item--active')) {
        // Makes sure that it keeps gracefully at least one single color rendered, in cases no available colors from middleware
        if (!availableColors.length) return;
        previousActiveColor = color;
      }
      return colorSwatchToRemove.parentNode.remove();
    });

    // Bring current variant data from product JSON and input ID value assigned in the input field form
    const currentVariantId = document.querySelector('form input[name="id"]').value;
    const currentVariantObj = productJsonData.variants.find((variant) => variant.id === Number(currentVariantId));

    // Get variants id and barcode according to available color after filtering colors were already rendered
    const colorVariants = availableColors.map((color) => {
      const { id, barcode } = productJsonData.variants.find((variant) => variant.options.includes(color));
      return { id, barcode };
    });

    // Build and append these new color swatches to its parement list element
    colorVariants.forEach((variant, index) => {
      const colorSwatchItem = this.#buildColorSwatch({
        availableColors,
        variant,
        index,
        currentVariantObj,
      });

      colorSwatchesList.appendChild(colorSwatchItem);
    });

    if (!previousActiveColor) {
      // We will move to check and update sizes
      this.#updateSizes(inventoryData, productJsonData);
      callback();
      // Reveal
      this.#revealHiddenElements();
      return;
    }

    // Go to choose the first available color and replace sizes variant ids whether they were linked to color that we got rid of it
    const nextAvailableColor = colorSwatchesList.querySelector('.pdp-c-atc-color-selector__swatch-item');

    nextAvailableColor?.classList.add('pdp-c-atc-color-selector__swatch-item--active');

    const sectionsToRender = getSectionsToRender();

    // Build a url to bring Shopify sections based on variant-id gotten from color swatch item
    const url = `${window.location.pathname}?variant=${nextAvailableColor.dataset.variantId}&sections=${sectionsToRender
      .map((sectionItem) => sectionItem.section)
      .join(',')}`;

    fetch(url)
      .then((response) => {
        if (!response.ok) {
          throw new Error('The PDP color change was not okay');
        }
        return response.json();
      })
      .then((sections) => {
        sectionsToRender.forEach((sectionItem) => {
          const section = document.querySelector(`#${sectionItem.id}`);
          section.innerHTML = getSectionInnerHTML(sections[sectionItem.section], sectionItem.selector);
        });

        // Repeating PDP Gallery JavaScript Logic
        gallery();
        this.#updateSizes(inventoryData, productJsonData);
        callback();
        this.#revealHiddenElements();
      })
      .catch((err) => {
        console.error(err.message);
      });

    return;
  };

  /**
   * Build a size watch item
   * @param {Object} params - parameter object
   * @param {Object} params.sizeVariant - variant object which contains id, sku, barcode, and options
   * @param {Object} params.inventoryData - inventory data fetched from inventory.js
   * @param {String} params.activeVariantSize - active size variant id
   * @param {Array<Object>} params.sizesAccessibilityList - list of accessibility sizes values
   * @param {Array<Object>} params.variantScopedFinalSaleMetafieldData - variant-scoped final sale metafield data
   * @returns {String} HTML markup for a size watch item
   */
  #buildSizeWatch = ({
    sizeVariant,
    inventoryData,
    activeVariantSize,
    sizesAccessibilityList,
    variantScopedFinalSaleMetafieldData,
  }) => {
    return `
      <a
        id="size_selector_${sizeVariant.options.at(1).trim()}"
        class="pdp-c-atc-size-selector__size-item ${
          inventoryData.color[sizeVariant.id] === 0 ? 'pdp-c-atc-size-selector__size-item--sold-out' : ''
        } ${activeVariantSize === sizeVariant.id ? 'pdp-c-atc-size-selector__size-item--active' : ''}"
        data-variant-id="${sizeVariant.id}"
        data-availability="${inventoryData.color[sizeVariant.id] === 0 ? 'false' : 'true'}"
        data-sku="${sizeVariant.sku}"
        data-barcode="${sizeVariant.barcode}"
        data-price="${sizeVariant.price}"
        data-compare-at-price="${sizeVariant.compareAtPrice}"
        data-product-type="${sizeVariant.productType}"
        data-variant-scoped-final-sale-metafield='${
          variantScopedFinalSaleMetafieldData.find((item) => item.id === sizeVariant.id)?.variantFinalSaleMetafield ||
          ''
        }'
        aria-label="Size: ${
          sizesAccessibilityList.find((l) => l.size_code === sizeVariant.options.at(1).trim())?.aria_label
        }"
      >
        ${
          inventoryData.color[sizeVariant.id] === 0
            ? '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 79.125 34"><line x1="79.125" y1="0" x2="0" y2="34" stroke="#92959B" stroke-width="1"></line></svg>'
            : ''
        }
        ${sizeVariant.options.at(1).trim()}
      </a>
      ${
        inventoryData.color[sizeVariant.id] === 0
          ? `
        <div
          class="pdp-c-atc-size-selector__email-asset"
          data-variant-id="${sizeVariant.id}"
          data-availability="${inventoryData.color[sizeVariant.id] === 0 ? 'false' : 'true'}"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
            <path d="M2 2C1.4485 2 1 2.4485 1 3V9C1 9.5515 1.4485 10 2 10H10C10.5515 10 11 9.5515 11 9V3C11 2.4485 10.5515 2 10 2H2ZM2 3H10V3.00293L6 5.5L2 3.00195V3ZM2 4.00195L6 6.5L10 4.00293L10.001 9H2V4.00195Z" fill="#4A4E54"/>
          </svg>
        </div>
      `
          : ''
      }
    `;
  };

  /**
   * Build a size watch item
   * @param {Object} inventoryData - inventory data fetched from inventory.js
   * @param {Object} productJsonData - product JSON data fetched from product.js
   */
  #updateSizes = (inventoryData, productJsonData) => {
    // Get active color swatch
    const activeColorSwatch =
      document.querySelector('.pdp-c-atc-color-selector__swatch-item--active')?.dataset.color || '';
    // Get active size swatch rendered on page
    const previousActiveRenderedSizeSwatch =
      document.querySelector('.pdp-c-atc-size-selector__size-item--active')?.dataset.variantId || '';
    // Get variants id, sku, barcode, and options according to active color after be chosen
    const sizeVariants = productJsonData.variants
      .filter((variant) => variant.options.includes(activeColorSwatch.toUpperCase()))
      .map((variant) => ({
        id: variant.id.toString(),
        barcode: variant.barcode,
        sku: variant.sku,
        options: variant.options,
        price: variant.price,
        compareAtPrice: variant.compare_at_price,
        productType: productJsonData.type,
      }));
    // Get variant-scoped final sale metafield data
    const variantScopedFinalSaleMetafieldData = JSON.parse(
      document.querySelector('script[data-variant-scoped-final-sale-metafield-json]')?.innerHTML || '{}',
    );
    // Get big CTA button element
    const bigCtaBtn = document.querySelector('.pdp-c-atc__submit-btn');
    // Get list size wrapper element and clear it
    const sizeListWrapper = document.querySelector('.pdp-c-atc-size-selector__size-list');
    // Get accessibility size list
    const sizesAccessibilityList = JSON.parse(document.getElementById('accessibility-sizes-list')?.innerHTML);

    // Clear size list wrapper
    sizeListWrapper.innerHTML = '';

    // Variable to set active size & previous size selected
    const previousSelectedSize =
      productJsonData.variants
        .find((variant) => variant.id.toString() === window.location.search.match(/variant=([^&]*)/)?.[1])
        ?.options.at(1)
        .trim() || '';

    // Part 1: - We will first look for over all new sizes which one can qualify as new active size.
    // It is good when previously you selected XL and then it didn't bring any XL only XS, S, M, and L.
    // In case, it has gotten no insights, you must choose the first size available. Otherwise, it will look at Part 2 down below.
    let activeVariantSize = Array.from(sizeVariants).find(
      (sizeVariant) =>
        (!previousSelectedSize && inventoryData.color[sizeVariant.id] > 0) ||
        (previousSelectedSize && sizeVariant.options.at(1).trim() === previousSelectedSize),
    )?.id;

    // Part 2: - If in Part 1 we couldn't find any active size variant, we will check if there is any size available in stock.
    // Also, we want to make sure that there weren't any available sizes from the middleware to go back to choose the previous active rendered size swatch.
    const isThereAvailableSizes = Array.from(sizeVariants).some(
      (sizeVariant) => inventoryData.color[sizeVariant.id] > 0,
    );
    if (!activeVariantSize && !isThereAvailableSizes) {
      activeVariantSize = previousActiveRenderedSizeSwatch;
    }

    sizeVariants.forEach((sizeVariant) => {
      const wrapper = document.createElement('li');
      wrapper.innerHTML = this.#buildSizeWatch({
        sizeVariant,
        inventoryData,
        activeVariantSize,
        sizesAccessibilityList,
        variantScopedFinalSaleMetafieldData,
      });
      sizeListWrapper.appendChild(wrapper);

      // We can set properly the big black btn CTA state on PDP because we truly know where is current active size
      if (activeVariantSize === sizeVariant.id) {
        // Set the hidden input variant id value according to new active size chosen
        const inputHiddenVariantId = document.querySelector('form input[name="id"]');
        inputHiddenVariantId.value = sizeVariant.id;

        if (inventoryData.color[sizeVariant.id] === 0) {
          bigCtaBtn.classList.add('pdp-c-atc__submit-btn--waitlist');
          bigCtaBtn.querySelector('span').textContent = 'Join Waitlist';
          bigCtaBtn.dataset.waitlistOpen = 'true';
        } else {
          bigCtaBtn.classList.remove('pdp-c-atc__submit-btn--waitlist');
          bigCtaBtn.querySelector('span').textContent = 'Add to Bag';
          bigCtaBtn.dataset.waitlistOpen = 'false';
        }
      }
    });
  };

  /**
   * Check inventory availability
   * @param {Boolean} checkColors - flag to check color availability
   * @param {Function} callback - callback function to execute after inventory check
   * @returns {Promise}
   */
  checkInventory = (checkColors = false, callback = () => {}) => {
    if (window.theme.country_iso === 'US') {
      return callback();
    }

    // Get product JSON data
    const productJsonData = JSON.parse(document.querySelector('[data-product-json]').innerHTML);

    // If we already have inventory data saved in the instance, we will use it directly
    if (this.inventoryData) {
      return new Promise((resolve) => {
        // Check and update only sizes
        this.#updateSizes(this.inventoryData, productJsonData);
        // Update active variant
        callback();
        // Reveal
        this.#revealHiddenElements();
        resolve();
      }).catch((err) => console.error(`Error occurred while checking cached inventory: ${err}`));
    }

    inventory(productJsonData)
      .then((data) => {
        // Save inventory data to the instance
        this.inventoryData = data;
        if (checkColors) {
          // Check and update colors and potentially sizes too
          this.#updateColors(data, productJsonData, callback);
        } else {
          // Check and update only sizes
          this.#updateSizes(data, productJsonData);
          // Update active variant
          callback();
          // Reveal
          this.#revealHiddenElements();
        }
      })
      .catch((err) => console.error(`Error occurred while checking inventory: ${err}`));
  };
}

// Make CheckInventory available globally
if (typeof window !== 'undefined') {
  window.checkInventory = new CheckInventory();
}
